import React, { useState } from 'react';
import { adminProductApi } from '../../../../api/adminProductApi';
import { createProductDraft, toProductPayload, validateProductDraft } from './mobileProductUtils';
import useMobileMutation from './useMobileMutation';
import MobileProductDialog from './MobileProductDialog';
import MobileProductConfirmation from './MobileProductConfirmation';
import MobileProductFeedback from './MobileProductFeedback';
import MobileProductEditorFields from './MobileProductEditorFields';
import MobileProductImages from './MobileProductImages';
import MobileProductVariants from './MobileProductVariants';
import { MobileStockHistoryList } from './MobileStockHistory';

function EditorForm({
  product,
  parent,
  onNavigate,
  onParent,
  onClose,
  onSaved,
  categories,
  discountCategories,
  rates,
  resourcesError,
  onRetry,
}) {
  const [draft, setDraft] = useState(() => createProductDraft(product));
  const [baseline, setBaseline] = useState(() => JSON.stringify(createProductDraft(product)));
  const [tab, setTab] = useState('main');
  const [message, setMessage] = useState('');
  const [leaving, setLeaving] = useState(null);
  const [deactivating, setDeactivating] = useState(false);
  const [creationUnconfirmed, setCreationUnconfirmed] = useState(false);
  const mutation = useMobileMutation();
  const change = (key, value) => {
    setDraft(prev => ({ ...prev, [key]: value }));
    setMessage('');
    mutation.setError('');
  };
  const requestLeave = action => {
    if (!mutation.canClose()) return;
    if (JSON.stringify(draft) !== baseline) setLeaving(() => action);
    else action();
  };
  const closeEditor = () => {
    if (!mutation.canClose()) return;
    if (deactivating) setDeactivating(false);
    else requestLeave(onClose);
  };
  const save = () => {
    if (creationUnconfirmed) return;
    const error = validateProductDraft(draft);
    if (error) {
      mutation.setError(error);
      setTab(/Ціна|Знижка|закупівельну|валюті/.test(error) ? 'prices' : 'main');
      return;
    }
    const payload = toProductPayload(draft);
    mutation.run(
      () =>
        draft.id ? adminProductApi.updateProduct(payload) : adminProductApi.createProduct(payload),
      response => {
        const id = draft.id || response.data?.productId || response.data?.data?.productId;
        if (!id) {
          setCreationUnconfirmed(true);
          onSaved();
          mutation.setError(
            'Товар створено, але сервер не повернув ID. Оновіть список перед повторним створенням.',
          );
          return;
        }
        const quantity = response.data?.quantity ?? draft.quantity;
        const saved = { ...draft, id, quantity, originalQuantity: quantity, originalState: draft.state, quantityChangeReason: '' };
        setDraft(saved);
        setBaseline(JSON.stringify(saved));
        setMessage('Товар збережено');
        onSaved();
      },
    );
  };
  const deactivate = () =>
    mutation.run(
      () => adminProductApi.deleteProduct(draft.id),
      () => {
        onSaved();
        if (parent) onParent();
        else onClose();
      },
    );
  const tabs = [
    ['main', 'Основне'],
    ['prices', 'Ціни'],
    ['categories', 'Категорії'],
    ['images', 'Фото'],
    ...(draft.id
      ? [['history', 'Історія'], ...(!draft.parentId ? [['variants', 'Дочірні']] : [])]
      : []),
  ];
  return (
    <MobileProductDialog
      title={
        draft.id ? `Товар #${draft.id}` : draft.parentId ? 'Новий дочірній товар' : 'Новий товар'
      }
      subtitle={draft.name}
      onClose={closeEditor}
      busy={mutation.busy}
      onSubmit={() => {
        if (deactivating) {
          deactivate();
        } else if (!leaving) save();
      }}
      footer={
        <>
          <button
            type='button'
            className='btn btn-outline-secondary'
            disabled={mutation.busy}
            onClick={closeEditor}
          >
            Закрити
          </button>
          <button
            type='submit'
            className={deactivating ? 'btn btn-danger' : 'btn btn-dark'}
            disabled={mutation.busy || !!leaving || creationUnconfirmed}
          >
            {mutation.busy ? 'Збереження…' : deactivating ? 'Деактивувати товар' : 'Зберегти товар'}
          </button>
        </>
      }
    >
      {parent && (
        <button
          type='button'
          className='btn btn-outline-secondary mb-3'
          disabled={mutation.busy}
          onClick={() => requestLeave(onParent)}
        >
          ← До батьківського товару
        </button>
      )}
      <MobileProductFeedback
        error={mutation.error || resourcesError}
        message={message}
        onRetry={resourcesError && onRetry}
      />
      {deactivating ? (
        <MobileProductConfirmation
          message={`Деактивувати дочірній товар #${draft.id}? Незбережені зміни не будуть застосовані.`}
          confirmLabel='Деактивувати товар'
          disabled={mutation.busy}
          onConfirm={deactivate}
          onCancel={() => setDeactivating(false)}
        />
      ) : leaving ? (
        <MobileProductConfirmation
          message='Є незбережені зміни. Залишити форму?'
          confirmLabel='Залишити без збереження'
          onConfirm={leaving}
          onCancel={() => setLeaving(null)}
        />
      ) : (
        <>
          {draft.id && draft.parentId && (
            <button
              type='button'
              className='btn btn-outline-danger mb-3'
              disabled={mutation.busy}
              onClick={() => setDeactivating(true)}
            >
              Деактивувати дочірній товар
            </button>
          )}
          <nav className='mp-editor-tabs' aria-label='Розділи редагування'>
            {tabs.map(([key, label]) => (
              <button
                key={key}
                type='button'
                className={tab === key ? 'active' : ''}
                aria-pressed={tab === key}
                disabled={mutation.busy}
                onClick={event => {
                  setTab(key);
                  event.currentTarget.closest('.modal-body').scrollTop = 0;
                }}
              >
                {label}
              </button>
            ))}
          </nav>
          <fieldset disabled={mutation.busy}>
            {['main', 'prices', 'categories'].includes(tab) && (
              <MobileProductEditorFields
                tab={tab}
                draft={draft}
                change={change}
                categories={categories}
                discountCategories={discountCategories}
                rates={rates}
              />
            )}
            {tab === 'images' && (
              <MobileProductImages
                draft={draft}
                onChange={(images, persisted) => {
                  change('images', images);
                  if (persisted) {
                    setBaseline(previous => {
                      const saved = JSON.parse(previous);
                      saved.images = persisted.added
                        ? [...saved.images, ...persisted.added]
                        : saved.images.filter(image => image !== persisted.removed);
                      return JSON.stringify(saved);
                    });
                    onSaved();
                  }
                }}
                mutation={mutation}
              />
            )}
          </fieldset>
          {tab === 'history' && <MobileStockHistoryList productId={draft.id} mutation={mutation} />}
          {tab === 'variants' && (
            <MobileProductVariants
              product={draft}
              busy={mutation.busy}
              onEdit={variant => requestLeave(() => onNavigate(variant, JSON.parse(baseline)))}
              onCreate={() =>
                requestLeave(() => onNavigate({ parentId: draft.id }, JSON.parse(baseline)))
              }
            />
          )}
        </>
      )}
    </MobileProductDialog>
  );
}

export default function MobileProductEditor({ product = {}, ...props }) {
  const [target, setTarget] = useState(product);
  const [parent, setParent] = useState(null);
  return (
    <EditorForm
      key={target.id || `new-${target.parentId || 'root'}`}
      product={target}
      parent={parent}
      onNavigate={(next, current) => {
        setParent(current);
        setTarget(next);
      }}
      onParent={() => {
        setTarget(parent);
        setParent(null);
      }}
      {...props}
    />
  );
}
